import AccessTimeIcon from '@mui/icons-material/AccessTime';
import {css, SvgIcon, Tooltip, Typography, useTheme} from '@mui/material';
import {DateUtils, GetStylesFunction, TranslateUtils} from '@wallaby-ui/core';
import * as React from 'react';
import {useIntl} from 'react-intl';

type DateType = 'lastProcessing' | 'loading' | 'currentProcessing';

interface OwnProps {
  date?: string;
  dateType: DateType;
  isNavigationOpen: boolean;
}

type Props = OwnProps;

const TOOLTIP_LABEL_KEY: Record<DateType, string> = {
  lastProcessing: 'processedAt.tooltip.lastProcessed',
  loading: 'processedAt.status.loading',
  currentProcessing: 'processedAt.tooltip.currentProcessing',
};

export const NavigationExportProcessingInfo: React.FC<Props> = (props) => {
  const {date, dateType, isNavigationOpen} = props;

  const theme = useTheme();
  const styles = getStyles({theme, params: {dateType}});
  const intl = useIntl();

  const formattedDate = date ? DateUtils.getFormattedDate(date, intl, false, false) : null;
  const tooltipLabel = TranslateUtils.translate(TOOLTIP_LABEL_KEY[dateType], intl);

  /* 'loading' icon shows the label both inline and in the tooltip — no date is involved.
     The other two date-bearing types show the formatted date inline (or '-' when missing)
     and append it to the tooltip when navigation is collapsed. */
  const inlineContent = dateType === 'loading' ? tooltipLabel : formattedDate ?? '-';
  const tooltipText =
    dateType === 'loading' || isNavigationOpen ? tooltipLabel : `${tooltipLabel}: ${inlineContent}`;

  return (
    <Tooltip title={tooltipText}>
      <div css={styles.wrapper}>
        <SvgIcon component={AccessTimeIcon} css={styles.accessTimeIcon} />
        {isNavigationOpen && <Typography>{inlineContent}</Typography>}
      </div>
    </Tooltip>
  );
};

NavigationExportProcessingInfo.displayName = 'NavigationExportProcessingInfo';

const getStyles: GetStylesFunction = (options) => {
  const dateTypeParam = options?.params?.dateType;
  return {
    wrapper: css({
      display: 'flex',
      alignItems: 'center',
      gap: options?.theme?.spacing(2),
    }),
    accessTimeIcon: css({
      height: 16,
      width: 16,
      color: dateTypeParam === 'lastProcessing' ? '#A7F0BA' : '#FFA726',
    }),
  };
};
